import { FlatHierarchy, IPNSProps, issuePriortyEnums, onIssueChange, TIssueStatus } from "@mctypes/global.types";
import { IBoardBucket, IBoardType } from "@schemas/issueBoardSchema";
import { IIssueApi } from "@schemas/issueSchema";
import { useMemo } from "react";
import { McDropdown, McInput, McTitle, SimpleTagsDeprecated } from "../";
import { useGetSingleIssueBoardQuery } from "../../app/routes/issuesApp/IssueBoardRTK";
import {
  ISSUE_COMPLETED_VALUE,
  ISSUE_PRIORTY_OPTIONS,
  ISSUE_STATES_OPTIONS,
} from "../../app/routes/issuesApp/issueConstants";
import { IssueDisabledColumnsEnum } from "../../mc-shared/enums";
import McTypeahead from "../McTypeahead/McTypeahead";

const IssueDetailsSection: React.FC<{
  issue: IIssueApi;
  showConclusion: boolean;
  boardBuckets: IBoardBucket[];
  boardTypes: IBoardType[];
  onChange: onIssueChange;
  onTagChange: (oldValue: string[], newValue: string[]) => void;
  tagOptions: string[];
  allowNewTags?: boolean;
  pnsItems: FlatHierarchy<IPNSProps>;
  disabledColumns?: string[];
}> = ({
  issue,
  showConclusion,
  boardBuckets,
  boardTypes,
  onChange,
  onTagChange,
  tagOptions,
  allowNewTags = true,
  pnsItems,
  disabledColumns,
}) => {
  const { data: issueBoard } = useGetSingleIssueBoardQuery({
    issueBoardId: issue.issueBoard._id,
    projectId: issue.project,
  });

  const typeAheadPNSItems = useMemo(() => {
    return (pnsItems || []).map((pns) => ({
      ...pns,
      comboLabel: `${pns.props.pnsId} - ${pns.props.title}`,
    }));
  }, [pnsItems]);

  const defaultSelectedPNS = useMemo(() => {
    const filteredItems = (typeAheadPNSItems || []).filter((p) => p.comboLabel === issue.pns);

    return filteredItems.length > 0
      ? filteredItems
      : [
          {
            comboLabel: issue.pns ?? "",
            _id: "",
            parentId: "",
            props: {
              pnsId: "",
              title: "",
            },
          },
        ];
  }, [typeAheadPNSItems, issue.pns]);

  return (
    <div className="d-flex flex-column">
      <div className="even-columns mt-2">
        <div className="d-flex flex-column mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.STATUS) === false && (
            <McTitle title="Status">
              <McDropdown
                currentValue={ISSUE_STATES_OPTIONS.find((s) => s.value === issue.status)}
                options={ISSUE_STATES_OPTIONS}
                type="colordot"
                displayKey="value"
                onChange={(oldValue, newValue) => onChange(oldValue.value, newValue.value as TIssueStatus, "status")}
              />
            </McTitle>
          )}
        </div>
        <div className="d-flex flex-column mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.PRIORITY) === false && (
            <McTitle title="Prioritet">
              <McDropdown
                currentValue={ISSUE_PRIORTY_OPTIONS.find((p) => p.key === issue?.priority)}
                options={ISSUE_PRIORTY_OPTIONS}
                type="colordot"
                displayKey="displayName"
                onChange={(oldValue, newValue) =>
                  onChange(oldValue?.key, newValue?.key as issuePriortyEnums, "priority")
                }
              />
            </McTitle>
          )}
        </div>
        <div className="d-flex flex-column mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.REFERENCEURL) === false && (
            <McTitle title="Link">
              <McTitle.InlineData positionInlineData="flex-end">
                {/https?:\/\/.*/i.test(issue.referenceUrl) === true ? (
                  <a href={issue.referenceUrl} target="_blank" rel="noreferrer">
                    <i className="fa fa-external-link-square fa-fw" />
                  </a>
                ) : (
                  <i className="fa fa-external-link-square fa-fw gray-400-text" />
                )}
              </McTitle.InlineData>

              <McInput
                placeholder="http://"
                onBlur={(e) => onChange(issue.referenceUrl, e.target.value, IssueDisabledColumnsEnum.REFERENCEURL)}
                value={issue.referenceUrl}
              />
            </McTitle>
          )}
        </div>
      </div>
      <div className="even-columns mt-2">
        <div className="d-flex flex-column mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.BUCKET) === false && (
            <McTitle title={issueBoard?.bucketNameSetting}>
              <McDropdown
                currentValue={boardBuckets.find((b) => b.value === issue.bucket) || { value: issue.bucket }}
                options={boardBuckets}
                displayKey="value"
                onChange={(oldValue, newValue) => onChange(oldValue?.value, newValue?.value, "bucket")}
                disabled={issue.taskOrIssue === "TASK"}
              />
            </McTitle>
          )}
        </div>
        <div className="d-flex flex-column mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.TYPEOFISSUE) === false && (
            <McTitle title="Type">
              <McDropdown
                currentValue={boardTypes.find((t) => t.name === issue.typeOfIssue)}
                options={boardTypes}
                displayKey="name"
                onChange={(oldValue, newValue) =>
                  onChange(oldValue?.name, newValue?.name, IssueDisabledColumnsEnum.TYPEOFISSUE)
                }
              />
            </McTitle>
          )}
        </div>
        <div className="d-flex flex-column mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.PNS) === false && (
            <McTitle title="PNS">
              <McTypeahead
                id="issuepnstypeahead"
                clearButton
                labelKey="comboLabel"
                options={typeAheadPNSItems.map((item) => {
                  return { comboLabel: `${item.props.pnsId} - ${item.props.title}` };
                })}
                defaultSelected={defaultSelectedPNS ?? []}
                onChange={(items) => {
                  onChange(issue.pns, items[0]?.comboLabel || "", IssueDisabledColumnsEnum.PNS);
                }}
                allowNew
              />
            </McTitle>
          )}
        </div>
      </div>
      <div className="d-flex flex-row mt-2">
        <div className="d-flex flex-column w-100 mr-4">
          {disabledColumns?.includes(IssueDisabledColumnsEnum.DESCRIPTION) === false && (
            <McTitle title="Beskrivelse">
              <McInput
                onBlur={(e) => onChange(issue.description, e.target.value, "description")}
                value={issue.description}
                type="textarea"
                dynamicHeight={true}
              />
            </McTitle>
          )}
        </div>
      </div>
      {((issue.status === ISSUE_COMPLETED_VALUE && showConclusion === true) || showConclusion === false) && (
        <div className="d-flex flex-row mt-2">
          <div className="d-flex flex-column w-100 mr-4">
            <McTitle title="Konklusjon">
              <McInput
                onBlur={(e) => onChange(issue.conclusion, e.target.value, "conclusion")}
                value={issue.conclusion}
                type="textarea"
                dynamicHeight={true}
              />
            </McTitle>
          </div>
        </div>
      )}
      <div className="d-flex flex-row mt-2">
        <div className="d-flex flex-column w-100 mr-4">
          <McTitle title="Etiketter">
            <SimpleTagsDeprecated
              activeTags={issue.tags}
              tagOptions={tagOptions}
              allowNew={allowNewTags}
              onChange={(value) => onTagChange(issue.tags, value)}
            />
          </McTitle>
        </div>
      </div>
    </div>
  );
};

export default IssueDetailsSection;
